"use client";

import React, { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";

// 🔴 must match the same appId used elsewhere (articles)
const appId = "canada60";

const AdAnalyticsPage = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("clickCount");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const colRef = collection(
          db,
          "artifacts",
          appId,
          "public",
          "data",
          "custom_ads"
        );

        const q = query(colRef, orderBy("priority", "asc"));
        const snap = await getDocs(q);

        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setAds(list);
      } catch (e) {
        console.error("Error fetching ads:", e);
      }
      setLoading(false);
    };

    load();
  }, []);

  // ---------- derived metrics ----------
  const totalClicks = ads.reduce((s, a) => s + (a.clickCount || 0), 0);
  const totalImpressions = ads.reduce(
    (s, a) => s + (a.impressionCount || 0),
    0
  );
  const avgCtr =
    totalImpressions > 0
      ? ((totalClicks / totalImpressions) * 100).toFixed(2)
      : "0.00";

  const filteredAds = ads
    .filter(
      (a) =>
        a.headline?.toLowerCase().includes(search.toLowerCase()) ||
        a.advertiser?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "ctr") {
        const ctrA =
          a.impressionCount > 0
            ? a.clickCount / a.impressionCount
            : 0;
        const ctrB =
          b.impressionCount > 0
            ? b.clickCount / b.impressionCount
            : 0;
        return ctrB - ctrA;
      }
      return (b[sortBy] || 0) - (a[sortBy] || 0);
    });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 flex justify-center py-8">
      <div className="w-full max-w-5xl px-4 lg:px-8">
        {/* Back */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center text-slate-400 hover:text-white mb-6"
        >
          <ChevronLeft size={18} className="mr-1" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">
            Ad Click Analytics
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Performance overview of all in-app advertisements
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            ["Total Ads", ads.length],
            ["Total Clicks", totalClicks],
            ["Impressions", totalImpressions],
            ["Avg CTR", `${avgCtr}%`],
          ].map(([label, value]) => (
            <div
              key={label}
              className="bg-slate-900/70 border border-slate-800 rounded-xl p-4"
            >
              <p className="text-xs text-slate-400">{label}</p>
              <p className="text-2xl font-semibold text-white">
                {value}
              </p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ads or advertisers…"
            className="flex-1 bg-slate-900 border border-slate-800 rounded-lg px-4 py-2 text-sm text-white placeholder-slate-500"
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="clickCount">Sort by Clicks</option>
            <option value="impressionCount">Sort by Impressions</option>
            <option value="ctr">Sort by CTR</option>
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <p className="text-slate-400">Loading analytics data…</p>
        ) : (
          <div className="space-y-4">
            {filteredAds.map((ad) => {
              const ctr =
                ad.impressionCount > 0
                  ? (
                      (ad.clickCount / ad.impressionCount) *
                      100
                    ).toFixed(2)
                  : "0.00";

              return (
                <div
                  key={ad.id}
                  className="bg-slate-900/70 border border-slate-800 rounded-xl p-5"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <h3 className="font-semibold text-white">
                        {ad.headline || "Untitled Ad"}
                      </h3>
                      <p className="text-xs text-slate-400 mt-1">
                        {ad.advertiser || "—"}
                      </p>
                    </div>

                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        ad.isActive
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-red-500/10 text-red-400"
                      }`}
                    >
                      {ad.isActive ? "Active" : "Disabled"}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                    <div>
                      <p className="text-xs text-slate-400">
                        Clicks
                      </p>
                      <p className="text-lg font-semibold text-white">
                        {ad.clickCount ?? 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">
                        Impressions
                      </p>
                      <p className="text-lg font-semibold text-white">
                        {ad.impressionCount ?? 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">
                        CTR
                      </p>
                      <p className="text-lg font-semibold text-white">
                        {ctr}%
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredAds.length === 0 && (
              <p className="text-slate-500 text-sm">
                No ads match your search.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdAnalyticsPage;